/**
 * TestSelectionPanel — per-participant test picker for a draft assessment.
 *
 *  • Each test has a checkbox + result field(s).
 *  • Unchecked tests are stored as `enabled:false` so they NEVER show up
 *    on the final email/recap, even if a value was typed earlier.
 *  • Auto-saves on blur (text inputs) and on every checkbox toggle.
 *  • Empty values render as nothing in the email (handled server-side).
 *
 * Test schema must stay in sync with /app/backend/routes/assessment_sessions.py
 *   TEST_KEYS / TEST_FIELDS.
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPEED_TESTS = [
    { key: "shredmill", label: "Shredmill", fields: [
        { name: "resistance", label: "Resistance level", placeholder: "e.g. 6" },
        { name: "speed", label: "Speed (mph)", placeholder: "e.g. 8.5" },
    ]},
    { key: "dash_10", label: "10-Yard Dash", fields: [
        { name: "time", label: "Time (sec)", placeholder: "e.g. 1.65" },
    ]},
    { key: "dash_20", label: "20-Yard Dash", fields: [
        { name: "time", label: "Time (sec)", placeholder: "e.g. 2.95" },
    ]},
    { key: "max_vert", label: "Max Vert / Approach Jump", fields: [
        { name: "feet", label: "Feet", placeholder: "10" },
        { name: "inches", label: "Inches", placeholder: "4" },
    ]},
    { key: "vertical_jump", label: "Vertical Jump", fields: [
        { name: "height", label: "Jump height (in)", placeholder: "e.g. 28.5" },
    ]},
];

const STRENGTH_TESTS = [
    { key: "squat", label: "Squat" },
    { key: "single_leg", label: "Single Leg" },
    { key: "hinge", label: "Hinge" },
    { key: "push", label: "Push" },
    { key: "pull", label: "Pull" },
    { key: "core", label: "Core" },
];

function blankTests() {
    const out = {};
    for (const t of SPEED_TESTS) {
        out[t.key] = { enabled: false };
        for (const f of t.fields) out[t.key][f.name] = "";
    }
    for (const t of STRENGTH_TESTS) {
        out[t.key] = { enabled: false, notes: "" };
    }
    return out;
}

export default function TestSelectionPanel({ sessionId, participant, onUpdated }) {
    const [open, setOpen] = useState(true);
    const initial = { ...blankTests(), ...(participant.tests || {}) };
    const [tests, setTests] = useState(initial);
    const [extraNotes, setExtraNotes] = useState(participant.extra_notes || "");
    const [saving, setSaving] = useState(false);
    const saveTimer = useRef(null);

    // Sync local state back to server (debounced for text fields).
    const persist = async (nextTests, nextExtraNotes) => {
        setSaving(true);
        try {
            const r = await axios.patch(
                `${API}/coach/assessment-sessions/${sessionId}/participants/${participant.athlete_id}`,
                { tests: nextTests, extra_notes: nextExtraNotes },
                { withCredentials: true },
            );
            onUpdated?.(r.data);
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    const queueSave = (nextTests, nextExtraNotes) => {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => persist(nextTests, nextExtraNotes), 400);
    };

    useEffect(() => () => clearTimeout(saveTimer.current), []);

    const toggleTest = (key) => {
        setTests((prev) => {
            const next = { ...prev, [key]: { ...prev[key], enabled: !prev[key]?.enabled } };
            persist(next, extraNotes);
            return next;
        });
    };

    const setField = (key, field, value) => {
        setTests((prev) => {
            const next = { ...prev, [key]: { ...prev[key], [field]: value } };
            queueSave(next, extraNotes);
            return next;
        });
    };

    const setExtraNotesField = (value) => {
        setExtraNotes(value);
        queueSave(tests, value);
    };

    const count = Object.values(tests).filter((t) => t.enabled).length;

    return (
        <div className="border border-zinc-800 rounded-xl bg-zinc-950/40 overflow-hidden">
            <button
                type="button"
                data-testid={`tests-toggle-${participant.athlete_id}`}
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-900/40 transition-colors"
            >
                <div className="text-left">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Tests for {participant.name}</div>
                    <div className="text-[11px] text-zinc-400 mt-0.5">
                        {count === 0 ? "No tests selected yet" : `${count} test${count === 1 ? "" : "s"} selected`}
                        {saving && <span className="ml-2 inline-flex items-center gap-1 text-zinc-500"><Loader2 className="w-3 h-3 animate-spin" /> saving…</span>}
                    </div>
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-zinc-300" /> : <ChevronDown className="w-4 h-4 text-zinc-300" />}
            </button>

            {open && (
                <div className="px-4 pb-4 space-y-5">
                    <Group label="Speed & Power">
                        {SPEED_TESTS.map((t) => (
                            <TestRow
                                key={t.key}
                                testKey={t.key}
                                label={t.label}
                                enabled={tests[t.key]?.enabled}
                                onToggle={() => toggleTest(t.key)}
                                participantId={participant.athlete_id}
                            >
                                {t.fields.map((f) => (
                                    <input
                                        key={f.name}
                                        data-testid={`test-${t.key}-${f.name}-${participant.athlete_id}`}
                                        type="text"
                                        inputMode="decimal"
                                        value={tests[t.key]?.[f.name] || ""}
                                        onChange={(e) => setField(t.key, f.name, e.target.value)}
                                        placeholder={f.placeholder}
                                        disabled={!tests[t.key]?.enabled}
                                        className={`${inp} ${f.fields?.length > 1 ? "flex-1" : ""} disabled:opacity-50`}
                                    />
                                ))}
                            </TestRow>
                        ))}
                    </Group>

                    <Group label="Strength & Movement">
                        {STRENGTH_TESTS.map((t) => (
                            <TestRow
                                key={t.key}
                                testKey={t.key}
                                label={t.label}
                                enabled={tests[t.key]?.enabled}
                                onToggle={() => toggleTest(t.key)}
                                participantId={participant.athlete_id}
                            >
                                <input
                                    data-testid={`test-${t.key}-notes-${participant.athlete_id}`}
                                    type="text"
                                    value={tests[t.key]?.notes || ""}
                                    onChange={(e) => setField(t.key, "notes", e.target.value)}
                                    placeholder="Notes — what you saw, what to work on"
                                    disabled={!tests[t.key]?.enabled}
                                    className={`${inp} flex-1 disabled:opacity-50`}
                                />
                            </TestRow>
                        ))}
                    </Group>

                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-2">Extra notes</div>
                        <textarea
                            data-testid={`test-extra-notes-${participant.athlete_id}`}
                            rows={3}
                            value={extraNotes}
                            onChange={(e) => setExtraNotesField(e.target.value)}
                            placeholder="Anything else worth including in the recap email."
                            className={`${inp} w-full`}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

function Group({ label, children }) {
    return (
        <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">{label}</div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function TestRow({ testKey, label, enabled, onToggle, participantId, children }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-[1.4fr,2fr] gap-2 items-center border-l-2 border-zinc-800 pl-3 py-1">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                    type="checkbox"
                    data-testid={`test-toggle-${testKey}-${participantId}`}
                    checked={!!enabled}
                    onChange={onToggle}
                    className="w-4 h-4 accent-brand-red"
                />
                <span className={`text-sm ${enabled ? "text-white" : "text-zinc-400"}`}>{label}</span>
            </label>
            <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                {children}
            </div>
        </div>
    );
}

const inp = "bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red";
